import type { NativePlatform } from '#features/notifications/utils/nativePlatform';
import type { AppStatus, UpdateDecision } from '../model/types';

/**
 * Чистое решение «что показать». Вынесено из хука намеренно: это единственное место, где
 * ошибка стоит дорого (можно заблокировать приложение всем сразу), и его надо покрывать
 * тестами, а не проверять глазами на устройстве.
 */
export const decideUpdate = ({
  build,
  platform,
  status,
  dismissedAt,
  now,
  dismissWindowMs,
}: {
  /** Номер сборки из стора. `0` = неизвестен. */
  build: number;
  platform: NativePlatform;
  status: AppStatus | null;
  /** Когда человек закрыл баннер именно для этой целевой версии. */
  dismissedAt: number | null;
  now: number;
  dismissWindowMs: number;
}): UpdateDecision => {
  // Три причины ничего не показывать, и все три — «мы не знаем», а не «всё хорошо».
  // Fail-open: неизвестность никогда не блокирует.
  if (!status || build <= 0 || platform === 'web') {
    return { kind: 'none' };
  }

  const gate = platform === 'ios' ? status.ios : status.android;

  if (gate.min > 0 && build < gate.min) {
    return { kind: 'block', min: gate.min, storeUrl: gate.storeUrl };
  }

  if (gate.recommended > 0 && build < gate.recommended) {
    if (dismissedAt !== null && now - dismissedAt < dismissWindowMs) {
      return { kind: 'none' };
    }
    return { kind: 'banner', target: gate.recommended, storeUrl: gate.storeUrl };
  }

  return { kind: 'none' };
};

/**
 * Устарела ли сборка по мнению конфига. Нужно отдельно от `decideUpdate`, чтобы правильно
 * назвать человеку причину отказа шлюза: если версия свежая, а запрос всё равно отклонён на
 * валидации, это НАШ баг, и писать «обновите приложение» — врать (обновляться некуда).
 */
export const isBuildOutdated = ({
  build,
  platform,
  status,
}: {
  build: number;
  platform: NativePlatform;
  status: AppStatus | null;
}): boolean => {
  if (!status || build <= 0 || platform === 'web') {
    return false;
  }
  const gate = platform === 'ios' ? status.ios : status.android;
  const target = Math.max(gate.min, gate.recommended);
  return target > 0 && build < target;
};
